import type { CSSProperties, JSX, ReactNode } from 'react';
import { LoginHistoryList, type AuditSession } from '../LoginHistoryList';
import { Collapsible } from './Collapsible';
import { RoleGate, type ViewerTier } from './roles';
import type { AdminSection } from './types';

/**
 * The standard Security body: passkey management (injected — endpoints differ per
 * portal; the portal wraps it in a <Collapsible/> where it wants it foldable) and
 * the registrar-only login/activity audit as a `superuser` sub-control, rendered
 * collapsed by default so it never dominates the drawer. Presentational; the
 * portal supplies data. "Sign out all devices" lives in the Account section (so
 * it's on every portal), not here.
 */
export interface SecuritySectionProps {
  viewer: ViewerTier;
  /** Passkey management node (Vetting's <PasskeysCard/> today). Omit where absent. */
  passkeys?: ReactNode;
  /** Registrar-only audit: pass sessions (null = loading)… */
  sessions?: AuditSession[] | null;
  sessionsError?: string;
  /** …or a pre-rendered history node (Corpus's self-fetching panel). */
  historySlot?: ReactNode;
  accent?: string;
}

const muted: CSSProperties = {
  fontSize: 13,
  color: 'var(--color-muted, #6b7280)',
  margin: '8px 0',
};

export function SecuritySection({
  viewer,
  passkeys,
  sessions,
  sessionsError,
  historySlot,
  accent,
}: SecuritySectionProps): JSX.Element {
  const hasAudit = historySlot != null || sessions !== undefined || sessionsError != null;
  return (
    <div>
      {passkeys}
      <RoleGate viewer={viewer} min="superuser">
        {hasAudit && (
          <div style={{ marginTop: passkeys != null ? 14 : 0 }}>
            <Collapsible
              title="Recent sign-ins"
              hint={sessions != null ? String(sessions.length) : undefined}
            >
              {historySlot ??
                (sessionsError != null ? (
                  <p style={{ ...muted, color: 'var(--color-danger, #b3261e)' }}>{sessionsError}</p>
                ) : sessions == null ? (
                  <p style={muted}>Loading…</p>
                ) : (
                  <LoginHistoryList sessions={sessions} {...(accent ? { accent } : {})} />
                ))}
            </Collapsible>
          </div>
        )}
      </RoleGate>
    </div>
  );
}

/** Convenience factory → an AdminSection (Security, `user`-tier by default). */
export function securitySection(
  opts: Omit<SecuritySectionProps, 'viewer'> & {
    key?: string;
    title?: string;
    icon?: ReactNode;
    minRole?: ViewerTier;
  },
): AdminSection {
  const { key, title, icon, minRole, ...body } = opts;
  return {
    key: key ?? 'security',
    title: title ?? 'Security',
    ...(icon ? { icon } : {}),
    minRole: minRole ?? 'user',
    body: (ctx) => <SecuritySection viewer={ctx.viewer} {...body} />,
  };
}
